import React, { useEffect, useMemo, useState } from 'react';
import { useAorText } from 'utils/aorTerminology';
import { useParams } from 'react-router';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { isComboboxDismissal } from '@/components/ui/multi-id-combobox';
import { ListFilter } from 'lucide-react';
import {
  CHECK_IN_STATUS_COLUMNS,
  expandCheckInStatusFilters,
  isAssignedToIncident,
  matchesCheckInStatusFilter,
} from './checkInStatus';
import { type ResourceRow } from './ResourceTabCard';
import { AssetsFilterBar } from './AssetsFilterBar';
import { DhsResourceAddDialog } from './DhsResourceAddDialog';
import { AssetsCheckInBoard } from './AssetsCheckInBoard';
import { DhsResourceTable } from './DhsResourceTable';
import { DhsResourceDetailsDialog } from './DhsResourceDetailsDialog';
import { AssetImportBanner } from './AssetImportBanner';
import type { AssetMapSymbolControls } from './AssetMapSymbolCheckbox';
import { useHiddenAssetSymbols } from '../EventsTab/useHiddenAssetSymbols';
import { assetHasMapSymbol } from '../LayersTab/orgAssetPins';
import { ASSET_STATUS_FILTER_OPTIONS } from './resourceFilterOptions';
import { useGroupGuid } from 'components/IAP/hooks/commonHooks';
import { useAllIncidents } from 'components/IAP/hooks/incidentHooks';
import { useGetRostersForGroup } from 'hooks/incident/useRoster';
import { useAors } from 'hooks/useAors';
import { USCGResourceKindOptions } from 'components/IncidentConfig/ResourceTypes';
import {
  type ResourceSet,
  type ResourceSetFilterSelection,
  useArchiveResourceSets,
  usePaginatedResourceSets,
  useResourceSets,
} from '../../../IAP/hooks/resourceSetHooks';
import { ConfirmDeleteDialog } from 'components/Reports/ConfirmDeleteDialog';
import { toast } from 'react-toastify';
import { useOrgEventAutomationRules } from 'hooks/orgEvents/orgEventAutomationHooks';
import { resourceSetToRow } from './resourceSetToRow';
import { ResourceInIncident } from 'components/IAP/hooks/resourceInIncidentHooks';
import { CfaHeleneAssetItem } from '../NotificationsTab/CfaHeleneAssetItem';
import { useUscgMapContext } from '../LayersTab/UscgMapContext';
import { openResourcePopupOnMap } from './dhsResourceDisplay';
import { DEMO_ASSET_NOTES_PREFIXES, isSeededDemoAsset } from './demoAssets';

const RESOURCES_PER_PAGE = 10;

/** Page numbers + ellipsis, matching react-paginate margin=2 / range=3. */
export function resourcePageItems(
  current: number,
  total: number
): Array<number | 'ellipsis'> {
  if (total <= 0) return [];
  const pages = new Set<number>();
  for (let i = 1; i <= Math.min(2, total); i++) pages.add(i);
  for (let i = Math.max(1, total - 1); i <= total; i++) pages.add(i);
  for (let i = current - 1; i <= current + 1; i++) {
    if (i >= 1 && i <= total) pages.add(i);
  }
  const sorted = [...pages].sort((a, b) => a - b);
  const items: Array<number | 'ellipsis'> = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) items.push('ellipsis');
    items.push(sorted[i]);
  }
  return items;
}
const WORK_AVAILABILITY_FILTER_OPTIONS = [...CHECK_IN_STATUS_COLUMNS];
const REQUEST_STATUS_FILTER_OPTIONS = ['Requested', 'Not Requested'];

function resourceMatchesBoardFilters(
  resourceSet: ResourceSet,
  filterSelection: ResourceSetFilterSelection,
  searchTerm: string,
  aors: { id: string; name?: string }[]
): boolean {
  const query = searchTerm.trim().toLowerCase();
  if (query && !resourceSet.name.toLowerCase().includes(query)) return false;
  if (
    filterSelection.kind.length > 0 &&
    !filterSelection.kind.includes(resourceSet.kind)
  ) {
    return false;
  }
  if (filterSelection.aor.length > 0) {
    const aorName = aors.find((aor) => aor.id === resourceSet.aor)?.name;
    if (!aorName || !filterSelection.aor.includes(aorName)) return false;
  }
  if (filterSelection.incident.length > 0) {
    const incidentIds = [
      ...(resourceSet.incident_details?.map((incident) => incident.id) ?? []),
      ...(resourceSet.incidents ?? []),
    ];
    if (!filterSelection.incident.some((id) => incidentIds.includes(id))) {
      return false;
    }
  }
  if (filterSelection.workAvailability.length > 0) {
    const status = resourceSet.current_status || resourceSet.status;
    if (
      !matchesCheckInStatusFilter(
        status,
        filterSelection.workAvailability,
        isAssignedToIncident(resourceSet)
      )
    ) {
      return false;
    }
  }
  if (filterSelection.requestStatus.length > 0) {
    const label = resourceSet.in_request ? 'Requested' : 'Not Requested';
    if (!filterSelection.requestStatus.includes(label)) return false;
  }
  if (filterSelection.assetStatus.length > 0) {
    if (!filterSelection.assetStatus.includes(resourceSet.asset_status || '')) {
      return false;
    }
  }
  return true;
}

const ResourceView: React.FC<{
  showAddAsset: boolean;
  setShowAddAsset: (show: boolean) => void;
  handleResourceUpdate: (updatedResource: Partial<ResourceInIncident>) => void;
  searchTerm: string;
}> = ({ showAddAsset, setShowAddAsset, searchTerm }) => {
  const aorText = useAorText();
  const [view, setView] = useState<'table' | 'list' | 'board'>('table');
  const [selectedRow, setSelectedRow] = useState<ResourceRow | null>(null);
  const [dialogMode, setDialogMode] = useState<'view' | 'edit'>('view');
  const [openedInEdit, setOpenedInEdit] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { data: incidents = [] } = useAllIncidents();
  const { data: aors = [] } = useAors();
  const { mapView } = useUscgMapContext();

  const groupId = useGroupGuid();
  // Fetch group members
  const { data: groupMembers = [] } = useGetRostersForGroup(groupId);

  const kindFilterOptions = useMemo(
    () => USCGResourceKindOptions.map((option) => option.value),
    []
  );
  const aorFilterOptions = useMemo(
    () => aors.map((aor) => aor.name).filter(Boolean),
    [aors]
  );
  const incidentFilterOptions = useMemo(
    () =>
      incidents
        .filter((incident) => !!incident.id && !!incident.name)
        .map((incident) => ({ value: incident.id, label: incident.name })),
    [incidents]
  );

  const columns = useMemo(() => {
    const generatedColumns = [
      {
        key: 'name',
        label: 'Resource Name',
        filterOptions: [],
      },
      {
        key: 'kind',
        label: 'Kind',
        filterOptions: kindFilterOptions,
      },
      {
        key: 'aor',
        label: aorText('AOR'),
        filterOptions: aorFilterOptions,
      },
      {
        key: 'incident',
        label: 'Incident',
        filterOptions: incidentFilterOptions,
      },
      {
        key: 'workAvailability',
        label: 'Current Operational Period Work Availability',
        filterOptions: WORK_AVAILABILITY_FILTER_OPTIONS,
      },
      {
        key: 'requestStatus',
        label: 'Request Status',
        filterOptions: REQUEST_STATUS_FILTER_OPTIONS,
      },
      {
        key: 'assetStatus',
        label: 'Asset Status',
        filterOptions: ASSET_STATUS_FILTER_OPTIONS,
      },
    ];
    return generatedColumns;
  }, [aorFilterOptions, incidentFilterOptions, kindFilterOptions, aorText]);

  // In the incident workspace (/iap/:incidentId) default the Incident filter to the current incident so the table
  // opens scoped to it. The raw route param (not the Redux fallback) keeps the corporate/map view unfiltered.
  const { incidentId: routeIncidentId } = useParams<{ incidentId: string }>();
  const [filterSelection, setFilterSelection] =
    useState<ResourceSetFilterSelection>({
      name: [],
      kind: [],
      aor: [],
      incident: routeIncidentId ? [routeIncidentId] : [],
      workAvailability: [],
      requestStatus: [],
      assetStatus: [],
      resultsPerPage: RESOURCES_PER_PAGE,
      pageNumber: 1,
    });

  // Seeded demo assets keep powering demo notifications, map pins and rules,
  // but never show in this tab.
  const { data: resourceSetsPage } = usePaginatedResourceSets({
    ...filterSelection,
    workAvailability: expandCheckInStatusFilters(
      filterSelection.workAvailability
    ),
    excludeNotesPrefix: DEMO_ASSET_NOTES_PREFIXES,
  });
  const { data: everyResourceSet } = useResourceSets();
  // The paginated table query already leaves out archived and demo assets;
  // the board reads this list.
  const allResourceSets = useMemo(
    () =>
      (everyResourceSet ?? []).filter(
        (resourceSet) =>
          !resourceSet.archived && !isSeededDemoAsset(resourceSet)
      ),
    [everyResourceSet]
  );
  const { data: automationRules = [] } = useOrgEventAutomationRules();

  useEffect(() => {
    const nextName = searchTerm.trim() ? [searchTerm.trim()] : [];
    setFilterSelection((prev) => {
      const same =
        prev.name.length === nextName.length &&
        prev.name.every((value, index) => value === nextName[index]);
      if (same) return prev;
      return { ...prev, name: nextName, pageNumber: 1 };
    });
  }, [searchTerm]);

  const filteredResourceSets = useMemo(() => {
    const query = searchTerm.toLowerCase();
    return (resourceSetsPage?.rows ?? []).filter((resourceSet) =>
      resourceSet.name.toLowerCase().includes(query)
    );
  }, [resourceSetsPage, searchTerm]);

  const memoizedResources = useMemo<ResourceRow[]>(
    () =>
      filteredResourceSets.map((resourceSet) =>
        resourceSetToRow(resourceSet, groupMembers, automationRules)
      ),
    [automationRules, groupMembers, filteredResourceSets]
  );

  const boardResourceSets = useMemo(
    () =>
      allResourceSets.filter((resourceSet) =>
        resourceMatchesBoardFilters(
          resourceSet,
          filterSelection,
          searchTerm,
          aors
        )
      ),
    [allResourceSets, aors, filterSelection, searchTerm]
  );

  const boardRows = useMemo<ResourceRow[]>(
    () =>
      boardResourceSets.map((resourceSet) =>
        resourceSetToRow(resourceSet, groupMembers, automationRules)
      ),
    [automationRules, boardResourceSets, groupMembers]
  );

  useEffect(() => {
    if (!selectedRow?.resource_set_id) return;
    const mapped = [...memoizedResources, ...boardRows].find(
      (row) => row.resource_set_id === selectedRow.resource_set_id
    );
    if (!mapped) return;
    setSelectedRow(mapped);
  }, [boardRows, memoizedResources, selectedRow?.resource_set_id]);

  const resourceBySetId = useMemo(() => {
    const map = new Map<string, ResourceSet>();
    for (const resourceSet of [...filteredResourceSets, ...boardResourceSets]) {
      map.set(resourceSet.resource_set_id, resourceSet);
    }
    return map;
  }, [boardResourceSets, filteredResourceSets]);

  const { hiddenIds, setVisible, setManyVisible } = useHiddenAssetSymbols();
  const mapSymbols = useMemo<AssetMapSymbolControls>(
    () => ({
      hiddenIds,
      setVisible,
      setManyVisible,
      hasMapSymbol: (row) => {
        const resource = row.resource_set_id
          ? resourceBySetId.get(row.resource_set_id)
          : undefined;
        if (resource) return assetHasMapSymbol(resource);
        return row.latitude != null && row.longitude != null;
      },
    }),
    [hiddenIds, resourceBySetId, setManyVisible, setVisible]
  );

  const handleFilterChange = (filterType: string, values: string[]) => {
    if (!(filterType in filterSelection)) {
      return;
    }
    setFilterSelection((prev) => ({
      ...prev,
      [filterType]: values,
      pageNumber: 1,
    }));
  };

  // The incident workspace scopes the list to its own incident, so clearing
  // returns to that scope rather than to every asset in the org.
  const clearFilters = () => {
    setFilterSelection((prev) => ({
      ...prev,
      name: [],
      kind: [],
      aor: [],
      incident: routeIncidentId ? [routeIncidentId] : [],
      workAvailability: [],
      requestStatus: [],
      assetStatus: [],
      pageNumber: 1,
    }));
  };

  const hasActiveFilters = columns.some(({ key }) => {
    const value = filterSelection[key as keyof ResourceSetFilterSelection];
    if (key === 'incident' && routeIncidentId) return false;
    return Array.isArray(value) && value.length > 0;
  });

  const updateCurrentPage = (pageNumber: number) => {
    setFilterSelection((prev) => ({
      ...prev,
      pageNumber,
      resultsPerPage: RESOURCES_PER_PAGE,
    }));
  };

  const openDetails = (row: ResourceRow) => {
    setSelectedRow(row);
    setDialogMode('view');
    setOpenedInEdit(false);
  };

  const startEdit = (row: ResourceRow) => {
    setSelectedRow(row);
    setDialogMode('edit');
    setOpenedInEdit(true);
  };

  const handleMapClick = (row: ResourceRow) => {
    openResourcePopupOnMap({
      row,
      mapView,
      resourceBySetId,
      aors,
    });
  };

  const closeDialog = () => {
    setSelectedRow(null);
    setDialogMode('view');
    setOpenedInEdit(false);
  };

  const archiveResourceSets = useArchiveResourceSets();
  const [rowToDelete, setRowToDelete] = useState<ResourceRow | null>(null);
  const deleteName = rowToDelete?.name || 'this asset';

  const confirmDelete = () => {
    const row = rowToDelete;
    if (!row?.resource_set_id) return;
    archiveResourceSets.mutate([row.resource_set_id], {
      onSuccess: () => {
        setRowToDelete(null);
        if (selectedRow?.resource_set_id === row.resource_set_id) {
          closeDialog();
        }
        toast.success(`Deleted ${row.name || 'the asset'}.`);
      },
      onError: () => {
        toast.error(
          `Couldn't delete ${row.name || 'the asset'}. Please try again.`
        );
      },
    });
  };

  const totalPages = resourceSetsPage?.totalPages ?? 0;
  const currentPage = resourceSetsPage?.pageNumber || 1;

  return (
    <>
      <div className="tw:min-w-0">
        <DhsResourceAddDialog
          open={showAddAsset}
          onOpenChange={setShowAddAsset}
          groupMembers={groupMembers}
        />
        <AssetImportBanner />
        <div className="tw:my-2 tw:flex tw:items-center tw:justify-between tw:gap-2">
          <ToggleGroup
            variant="outline"
            size="sm"
            spacing={0}
            multiple={false}
            value={[view]}
            onValueChange={(next) => {
              const value = Array.isArray(next) ? next[0] : next;
              if (value === 'table' || value === 'list' || value === 'board') {
                setView(value);
              }
            }}
            aria-label="Resource view"
          >
            <ToggleGroupItem value="table">Table</ToggleGroupItem>
            <ToggleGroupItem value="list">List</ToggleGroupItem>
            <ToggleGroupItem value="board">Board</ToggleGroupItem>
          </ToggleGroup>
          <Popover
            open={filtersOpen}
            onOpenChange={(open, eventDetails) => {
              const fromCombobox =
                !open &&
                (eventDetails.reason === 'outside-press' ||
                  eventDetails.reason === 'focus-out') &&
                isComboboxDismissal(eventDetails.event);
              if (fromCombobox) {
                eventDetails.cancel();
                return;
              }
              setFiltersOpen(open);
            }}
          >
            <PopoverTrigger
              render={
                <Button
                  type="button"
                  variant="outline"
                  size="icon-sm"
                  aria-label="Filters"
                  aria-expanded={filtersOpen}
                  className="tw:relative"
                >
                  <ListFilter className="tw:h-4 tw:w-4" />
                  {hasActiveFilters ? (
                    <span
                      className="tw:absolute tw:right-1 tw:top-1 tw:size-1.5 tw:rounded-full tw:bg-primary"
                      aria-hidden
                    />
                  ) : null}
                </Button>
              }
            />
            <PopoverContent align="end" className="dark tw:w-80 tw:p-3">
              <AssetsFilterBar
                filters={(({
                  resultsPerPage: _rpp,
                  pageNumber: _pn,
                  ...rest
                }) => rest)(filterSelection)}
                onFilterChange={handleFilterChange}
                columns={columns}
                onClearAll={clearFilters}
                hasActiveFilters={hasActiveFilters}
              />
            </PopoverContent>
          </Popover>
        </div>
        {view === 'table' ? (
          <DhsResourceTable
            rows={memoizedResources}
            onSelectRow={openDetails}
            onEditRow={startEdit}
            onDeleteRow={setRowToDelete}
            onMapClick={handleMapClick}
            mapSymbols={mapSymbols}
          />
        ) : view === 'board' ? (
          <AssetsCheckInBoard
            rows={boardRows}
            resourceBySetId={resourceBySetId}
            onSelectRow={openDetails}
            mapSymbols={mapSymbols}
          />
        ) : (
          <div className="tw:min-w-0 tw:space-y-1.5">
            {memoizedResources.length === 0 ? (
              <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
                No resources
              </p>
            ) : (
              memoizedResources.map((row) => {
                const resource = row.resource_set_id
                  ? resourceBySetId.get(row.resource_set_id)
                  : undefined;
                if (!resource) return null;
                return (
                  <CfaHeleneAssetItem
                    key={row.id}
                    resource={resource}
                    row={row}
                    includeChecklist={false}
                    showThreatChecklistCoverage
                    onMapClick={() => handleMapClick(row)}
                    onEditClick={() => startEdit(row)}
                    mapSymbolAvailable={mapSymbols.hasMapSymbol(row)}
                    mapSymbolVisible={!hiddenIds.has(resource.resource_set_id)}
                    onMapSymbolVisibleChange={(visible) =>
                      setVisible(resource.resource_set_id, visible)
                    }
                  />
                );
              })
            )}
          </div>
        )}
        {view !== 'board' && totalPages > 1 && (
          <div className="dark tw:mt-2 tw:text-foreground">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    href={void 0}
                    onClick={(event) => {
                      event.preventDefault();
                      if (currentPage > 1) {
                        updateCurrentPage(currentPage - 1);
                      }
                    }}
                    aria-disabled={currentPage <= 1}
                    className={
                      currentPage <= 1
                        ? 'tw:pointer-events-none tw:opacity-50'
                        : undefined
                    }
                  />
                </PaginationItem>
                {resourcePageItems(currentPage, totalPages).map(
                  (item, index) =>
                    item === 'ellipsis' ? (
                      <PaginationItem key={`ellipsis-${index}`}>
                        <PaginationEllipsis />
                      </PaginationItem>
                    ) : (
                      <PaginationItem key={item}>
                        <PaginationLink
                          href={void 0}
                          isActive={item === currentPage}
                          onClick={(event) => {
                            event.preventDefault();
                            updateCurrentPage(item);
                          }}
                        >
                          {item}
                        </PaginationLink>
                      </PaginationItem>
                    )
                )}
                <PaginationItem>
                  <PaginationNext
                    href={void 0}
                    onClick={(event) => {
                      event.preventDefault();
                      if (currentPage < totalPages) {
                        updateCurrentPage(currentPage + 1);
                      }
                    }}
                    aria-disabled={currentPage >= totalPages}
                    className={
                      currentPage >= totalPages
                        ? 'tw:pointer-events-none tw:opacity-50'
                        : undefined
                    }
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </div>
      <DhsResourceDetailsDialog
        row={selectedRow}
        open={!!selectedRow}
        onOpenChange={(open) => {
          if (!open) closeDialog();
        }}
        mode={dialogMode}
        onModeChange={setDialogMode}
        openedInEdit={openedInEdit}
        groupMembers={groupMembers}
        onRowPatch={(patch) => {
          setSelectedRow((prev) => (prev ? { ...prev, ...patch } : prev));
        }}
        onDelete={setRowToDelete}
      />
      <ConfirmDeleteDialog
        open={!!rowToDelete}
        title={`Delete ${deleteName}?`}
        description="It will be removed from the Assets tab, the map, automation rules and reports. Past events and reports that mention it are kept."
        pending={archiveResourceSets.isPending}
        onOpenChange={(open) => {
          if (!open && !archiveResourceSets.isPending) setRowToDelete(null);
        }}
        onConfirm={confirmDelete}
      />
    </>
  );
};

export default ResourceView;
